import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, TextField, Button, 
  Paper, Alert, CircularProgress, Divider,
  FormControl, InputLabel, Select, MenuItem, SelectChangeEvent
} from '@mui/material';
import { auth, db } from '../../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { UserRole } from '../../contexts/AuthContext';

const ProfileCreationTool: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [userChecked, setUserChecked] = useState(false);
  const [profileExists, setProfileExists] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: 'Employee' as UserRole,
    department: 'HR'
  });

  // Check if the user is authenticated and if their profile exists
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        // Wait a bit to ensure auth is initialized
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const user = auth.currentUser;
        console.log('Current auth user:', user?.uid || 'No user');
        setCurrentUser(user);

        if (user) {
          try {
            // Check if profile exists
            const userDocRef = doc(db, 'users', user.uid);
            const userDocSnap = await getDoc(userDocRef);
            
            const exists = userDocSnap.exists();
            setProfileExists(exists);
            
            if (exists) {
              const data = userDocSnap.data();
              // Pre-fill form with existing data
              if (data.firstName) setFormData(prev => ({ ...prev, firstName: data.firstName }));
              if (data.lastName) setFormData(prev => ({ ...prev, lastName: data.lastName }));
              if (data.role) setFormData(prev => ({ ...prev, role: data.role }));
              if (data.department) setFormData(prev => ({ ...prev, department: data.department }));
            } else {
              // Pre-fill with email username
              if (user.email) {
                const username = user.email.split('@')[0];
                setFormData(prev => ({ ...prev, firstName: username }));
              }
            }
          } catch (err: any) {
            console.error('Error checking profile:', err);
            setError(`Error checking profile: ${err.message}`);
          }
        }
        
        setUserChecked(true);
      } catch (err: any) {
        console.error('Error in auth check:', err);
        setError(`Error checking authentication: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    checkUserStatus();
  }, []);

  // Handle form changes
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  // Create or update profile
  const handleCreateProfile = async () => {
    if (!currentUser) {
      setError('You must be logged in to create a profile');
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      setError('First name and last name are required');
      return;
    }

    setCreatingProfile(true);
    setError(null);
    setSuccess(false);

    try {
      // Construct profile data
      const profileData = {
        uid: currentUser.uid,
        email: currentUser.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        department: formData.department,
        updatedAt: new Date()
      };

      // Add creation date if creating new profile
      if (!profileExists) {
        Object.assign(profileData, { 
          createdAt: new Date(),
          permissions: getDefaultPermissions(formData.role)
        });
      }

      // Write to Firestore with merge option to update existing or create new
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, profileData, { merge: true });
      
      setSuccess(true);
      setProfileExists(true);
      
      // Add to localStorage as fallback
      localStorage.setItem('cachedUserProfile', JSON.stringify(profileData));
      
      // Force reload after 2 seconds
      setTimeout(() => {
        window.location.href = '/profile';
      }, 2000);
    } catch (err: any) {
      console.error('Error creating/updating profile:', err);
      setError(`Failed to ${profileExists ? 'update' : 'create'} profile: ${err.message}`);
    } finally {
      setCreatingProfile(false);
    }
  };

  // Helper function to get default permissions
  const getDefaultPermissions = (role: UserRole) => {
    switch (role) {
      case 'Administrator':
        return {
          viewEmployees: true,
          manageEmployees: true,
          viewPayroll: true,
          processPayroll: true,
          viewReports: true,
          manageReports: true,
          viewCompanies: true,
          manageCompanies: true,
          viewSettings: true,
          manageSettings: true,
          manageTaxSettings: true,
          manageUsers: true,
        };
      case 'Manager':
        return {
          viewEmployees: true,
          manageEmployees: true,
          viewPayroll: true,
          processPayroll: false,
          viewReports: true,
          manageReports: false,
          viewCompanies: true,
          manageCompanies: false,
          viewSettings: true,
          manageSettings: false,
          manageTaxSettings: false,
          manageUsers: false,
        };
      default:
        return {
          viewEmployees: false,
          manageEmployees: false,
          viewPayroll: false,
          processPayroll: false,
          viewReports: false,
          manageReports: false,
          viewCompanies: false,
          manageCompanies: false,
          viewSettings: false,
          manageSettings: false,
          manageTaxSettings: false,
          manageUsers: false,
        };
    }
  };

  const roles = [
    'Administrator',
    'Manager',
    'HR',
    'Employee',
    'Accountant'
  ];

  const departments = [
    'Engineering',
    'Product',
    'Design',
    'Marketing',
    'Sales',
    'HR',
    'Finance',
    'Operations',
  ];

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>Emergency Profile Creation Tool</Typography>
        <Typography color="text.secondary" paragraph>
          This tool fixes issues with user profiles by directly creating or updating your profile in the database.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : !currentUser ? (
          <Paper sx={{ p: 3, my: 3, bgcolor: 'error.light' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              You must be logged in to use this tool
            </Alert>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => window.location.href = '/login'}
            >
              Go to Login
            </Button>
          </Paper>
        ) : (
          <Box>
            <Paper sx={{ p: 3, my: 3 }}>
              <Typography variant="h6" gutterBottom>
                {profileExists ? 'Update Your Profile' : 'Create Your Profile'}
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                {profileExists 
                  ? 'Your profile exists but may need updating to fix issues. Make any changes below and click Update.'
                  : 'No profile was found for your user account. Fill in the details below to create one.'}
              </Alert>

              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleTextChange}
                  required
                  fullWidth
                />

                <TextField
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleTextChange}
                  required
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleSelectChange}
                    label="Role"
                  >
                    {roles.map(role => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    name="department"
                    value={formData.department}
                    onChange={handleSelectChange}
                    label="Department"
                  >
                    {departments.map(dept => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Profile {profileExists ? 'updated' : 'created'} successfully! Redirecting to profile page...
                  </Alert>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateProfile}
                  disabled={creatingProfile || !formData.firstName || !formData.lastName}
                  startIcon={creatingProfile ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {creatingProfile 
                    ? 'Processing...' 
                    : profileExists 
                      ? 'Update Profile' 
                      : 'Create Profile'}
                </Button>
              </Box>
            </Paper>

            <Paper sx={{ p: 3, my: 3, bgcolor: 'info.light' }}>
              <Typography variant="h6" gutterBottom>User Information</Typography>
              <Box sx={{ fontSize: '0.875rem' }}>
                <Box sx={{ mb: 1 }}><strong>User ID:</strong> {currentUser.uid}</Box>
                <Box sx={{ mb: 1 }}><strong>Email:</strong> {currentUser.email}</Box>
                <Box sx={{ mb: 1 }}><strong>Auth Provider:</strong> {currentUser.providerData[0]?.providerId || 'unknown'}</Box>
                <Box sx={{ mb: 1 }}><strong>Profile Status:</strong> {profileExists ? 'Exists' : 'Missing'}</Box>
              </Box>
            </Paper>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Troubleshooting Options</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => window.location.href = '/profile'}
              >
                Go to Profile
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.reload();
                }}
              >
                Clear Storage & Reload
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => {
                  localStorage.removeItem('cachedUserProfile');
                  window.location.reload();
                }}
              >
                Clear Profile Cache
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ProfileCreationTool; 